import { get } from 'lodash';
import {
  usePopulateMemo,
  usePopulate,
  useFind,
  useFindRecord,
} from 'src/api/APIHooks';

export const RIDE_HEALTH_REFERRAL_FORM = 'Ride Health Referral Form';

const configureRideHealthForm = (referenceJSON, {
  employeeEmail,
  rideHealthConfig,
  caseObj,
  contextModelId,
  contextType,
}) => {
  const ELEMENT_NAME_INSTRUCTIONS_HTML = 'questionInstructions';
  const email = encodeURIComponent(employeeEmail);
  const firstName = get(caseObj, 'person.first_name');
  const lastName = get(caseObj, 'person.last_name');
  const personId = get(caseObj, 'person.id');
  const auth_key = rideHealthConfig.authKey;
  const gcpUrl = rideHealthConfig.url;
  const passback = encodeURIComponent(`${contextType}:${contextModelId}`);
  const rideHealthURL = new URL(gcpUrl);
  const params = {
    auth_key,
    firstName,
    lastName,
    mrn: personId,
    passback,
    action: 'book',
  };
  if (email) {
    params.email = email;
  }
  rideHealthURL.search = new URLSearchParams(params).toString();
  const instructionEl = referenceJSON.configuration.pages[0].elements.find(
    (e) => e.name === ELEMENT_NAME_INSTRUCTIONS_HTML,
  );
  instructionEl.html = `
    To connect your client with Ride Health, click on this ` +
    `<a target="_new" href=${rideHealthURL}>link</a>. A new ` +
    'browser window will open where you\'ll be prompted to enter ' +
    'the client\'s information. Once you\'ve completed the steps ' +
    'there, return to this page to confirm the Ride Health form was ' +
    'submitted successfully.';
  return referenceJSON;
};

export const useRideHealthConfig = (
  context,
  contextType,
  rideHealthLinkMapping,
) => {
  try {
    if (contextType !== 'case' || !context) {
      return {
        rideHealthConfig: null,
        eligibleForRideHealth: false,
        caseObj: null,
        isLoading: false,
      };
    }
    const caseQuery = useFindRecord('case', context, {
      queryConfig: {
        enabled: Boolean(contextType === 'case' && context),
        placeholderData: undefined,
      },
    });
    const {
      data: caseResponse,
      isLoading: isLoadingCase,
    } = caseQuery;
    const caseObj = get(caseResponse, 'data.data');
    const personQuery = usePopulate('person', 'person', caseObj, {
      queryConfig: {
        enabled: Boolean(caseObj),
        placeholderData: undefined,
      },
    });
    const { isLoading: isLoadingPerson } = personQuery;

    const person = get(caseObj, 'person.id');
    const { data: insurancesData } = useFind(
      'insurance',
      { person, state: 'active', 'plan.plan_type': 'social' },
      {
        queryConfig: {
          enabled: Boolean(person),
          placeholderData: undefined,
        },
      },
    );

    usePopulateMemo(caseQuery.data, [personQuery]);

    window.datadogLogs.logger.info('useRideHealthConfig insurances data', insurancesData);
    const list = get(insurancesData, 'data.data', []);
    window.datadogLogs.logger.info('useRideHealthConfig list', list);
    const socialCareCoveragePlan = list.find((insurance) => rideHealthLinkMapping[insurance?.plan?.id]);
    window.datadogLogs.logger.info('useRideHealthConfig socialCareCoveragePlan', socialCareCoveragePlan);
    const rideHealthConfig = rideHealthLinkMapping[socialCareCoveragePlan?.plan?.id];
    window.datadogLogs.logger.info('useRideHealthConfig rideHealthConfig', rideHealthConfig);

    return {
      rideHealthConfig,
      eligibleForRideHealth: Boolean(rideHealthConfig),
      caseObj,
      isLoading: isLoadingPerson || isLoadingCase,
    };
  } catch (error) {
    // console.error('useRideHealthConfig', error);
    return {
      rideHealthConfig: null,
      eligibleForRideHealth: false,
      caseObj: null,
      isLoading: false,
    };
  }
};

const useRideHealth = ({
  employeeEmail,
  contextModelId,
  contextType,
  rideHealthLinkMapping,
  surveyJSON,
}) => {
  try {
    const {
      isLoading,
      rideHealthConfig,
      caseObj,
    } = useRideHealthConfig(
      contextModelId,
      contextType,
      rideHealthLinkMapping,
    );
    const updatedSurveyJSON = rideHealthConfig ? configureRideHealthForm(
      surveyJSON,
      {
        employeeEmail,
        rideHealthConfig,
        caseObj,
        contextModelId,
        contextType,
      },
    ) : null;

    return {
      isLoading,
      isReady: Boolean(rideHealthConfig),
      caseObj,
      rideHealthConfig,
      updatedSurveyJSON,
    };
  } catch (error) {
    window.datadogLogs.logger.error('useRideHealth', {
      error,
    });
    return {
      isLoading: false,
      isReady: false,
      caseObj: null,
      rideHealthConfig: null,
      updatedSurveyJSON: null,
    };
  }
};

export default useRideHealth;
